import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useSelector } from "react-redux";

const PayModeChart = () => {
  const donations = useSelector((state) => state.donation?.donations || []);
  const chartRef = useRef();

  useEffect(() => {
    if (!donations.length) return;

    let activeLabel = null;

    // ---------------------
    // Data grouping
    // ---------------------
    const grouped = donations.reduce((acc, d) => {
      const mode = d.paymentMode || d.payMode || d.payPurse || "Unknown";
      acc[mode] = (acc[mode] || 0) + 1;
      return acc;
    }, {});

    const data = Object.keys(grouped).map((key) => ({
      label: key,
      value: grouped[key],
    }));

    const width = 360;
    const height = 330;
    const radius = 130;

    d3.select(chartRef.current).selectAll("*").remove();

    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height + 140);

    // ---------------------
    // Global Tooltip
    // ---------------------
    let tooltip = d3.select("#d3-global-tooltip");
    if (tooltip.empty()) {
      tooltip = d3
        .select("body")
        .append("div")
        .attr("id", "d3-global-tooltip")
        .attr("class", "d3-tooltip");
    }

    const chartGroup = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const pie = d3.pie().value((d) => d.value);
    const arc = d3.arc().innerRadius(70).outerRadius(radius);
    const arcHover = d3.arc().innerRadius(70).outerRadius(radius + 8);

    // ---------------------
    // Donut slices
    // ---------------------
    const slices = chartGroup
      .selectAll("path")
      .data(pie(data))
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => color(d.data.label))
      .style("cursor", "pointer");

    slices
      .on("mouseenter", function (event, d) {
        d3.select(this).transition().duration(200).attr("d", arcHover);

        tooltip
          .style("display", "block")
          .html(`<b>${d.data.label}</b><br/>Count: ${d.data.value}`)
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 40 + "px");
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 40 + "px");
      })
      .on("mouseleave", function () {
        d3.select(this).transition().duration(200).attr("d", arc);
        tooltip.style("display", "none");
      });

    // ---------------------
    // Legend
    // ---------------------
    const itemsPerRow = 3;
    const itemWidth = 110;

    const legend = svg
      .append("g")
      .attr(
        "transform",
        `translate(${width / 2 - (itemsPerRow * itemWidth) / 2}, ${
          height + 25
        })`
      );

    const legendItems = legend
      .selectAll(".legend-item")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => {
        const row = Math.floor(i / itemsPerRow);
        const col = i % itemsPerRow;
        return `translate(${col * itemWidth}, ${row * 30})`;
      })
      .style("cursor", "pointer");

    legendItems
      .append("rect")
      .attr("width", 14)
      .attr("height", 14)
      .attr("rx", 3)
      .attr("fill", (d) => color(d.label));

    legendItems
      .append("text")
      .attr("x", 20)
      .attr("y", 11)
      .style("font-size", "13px")
      .text((d) => d.label);

    // ---------------------
    // Legend hover & click
    // ---------------------
    legendItems
      .on("mouseenter", (event, d) => {
        slices
          .transition()
          .duration(200)
          .style("opacity", (s) =>
            s.data.label === d.label ? 1 : 0.3
          );

        slices
          .filter((s) => s.data.label === d.label)
          .transition()
          .duration(200)
          .attr("d", arcHover);

        const sliceData = slices
          .data()
          .find((s) => s.data.label === d.label);

        const [cx, cy] = arc.centroid(sliceData);
        const svgRect = svg.node().getBoundingClientRect();

        tooltip
          .style("display", "block")
          .html(`<b>${d.label}</b><br/>Count: ${sliceData.data.value}`)
          .style("left", svgRect.left + cx + width / 2 + "px")
          .style("top", svgRect.top + cy + height / 2 - 40 + "px");
      })
      .on("mouseleave", () => {
        slices
          .transition()
          .duration(200)
          .style("opacity", 1)
          .attr("d", arc);

        tooltip.style("display", "none");
      })
      .on("click", (event, d) => {
        if (activeLabel === d.label) {
          activeLabel = null;
          slices.transition().duration(300).style("opacity", 1);
        } else {
          activeLabel = d.label;
          slices
            .transition()
            .duration(300)
            .style("opacity", (s) =>
              s.data.label === d.label ? 1 : 0.2
            );
        }
      });
  }, [donations]);

  return (
    <div
      ref={chartRef}
      style={{ width: "360px", height: "460px", position: "relative" }}
    />
  );
};

export default PayModeChart;